/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { api } from "../lib/apiClient";
import { X, AlertOctagon, TriangleAlert, CalendarClock, Radio, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

const SECTIONS = [
  { key: "issues",           label: "Raised Issues",         icon: AlertOctagon,   route: "/pre-match/raise-issue" },
  { key: "incidents",        label: "Critical Incidents",    icon: TriangleAlert,  route: "/match-day/incidents" },
  { key: "pre_match_tasks",  label: "Pre-Match Tasks",       icon: CalendarClock,  route: "/pre-match/tracker" },
  { key: "runsheet",         label: "Run Sheet Items",       icon: Radio,          route: "/match-day/runsheet" },
];

function itemTitle(sectionKey, doc) {
  if (sectionKey === "issues") return `${doc.category || "Issue"} · ${doc.location_main || ""} ${doc.location_sub || ""}`.trim();
  if (sectionKey === "incidents") return `${doc.category || "Incident"} · ${doc.location || ""}`;
  if (sectionKey === "pre_match_tasks") return doc.title || "Task";
  if (sectionKey === "runsheet") return doc.activity || "Run sheet item";
  return doc.title || doc.id;
}

function itemSubtitle(sectionKey, doc) {
  if (sectionKey === "issues") return doc.details;
  if (sectionKey === "incidents") return doc.description;
  if (sectionKey === "pre_match_tasks") return `${doc.department || ""} · ${doc.status || ""}`;
  if (sectionKey === "runsheet") return doc.lead_department;
  return "";
}

export default function PersonReferencesDrawer({ matchId, person, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!person || !matchId) return;
    setLoading(true);
    api
      .get(`/matches/${matchId}/references/${person.id}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [person, matchId]);

  if (!person) return null;

  const totalCount = data
    ? SECTIONS.reduce((s, sec) => s + (data[sec.key]?.length || 0), 0)
    : 0;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="person-refs-drawer">
      <div className="flex-1 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <aside className="w-full md:w-[560px] bg-obsidian border-l border-cmdBorder flex flex-col shadow-2xl">
        <div className="p-5 border-b border-cmdBorder flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="mc-label">Where this person is tagged</div>
            <h2 className="font-head font-black text-2xl uppercase tracking-tight leading-none mt-1 truncate">{person.name}</h2>
            {person.dept_designation && <div className="text-xs text-zinc-400 mt-1">{person.dept_designation}</div>}
            {person.contact_no && <div className="font-mono-live text-xs text-zinc-500 mt-0.5">{person.contact_no}</div>}
          </div>
          <button
            onClick={onClose}
            className="border border-cmdBorder hover:border-white p-2 shrink-0"
            data-testid="person-refs-close"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading && <div className="p-8 text-zinc-500 font-mono-live">▓▒░ loading references ░▒▓</div>}

          {!loading && data && totalCount === 0 && (
            <div className="p-8 text-center text-zinc-500">
              <div className="text-sm">This person hasn't been tagged in any live item yet.</div>
              <div className="text-xs mt-2">They'll show up here as soon as they're tagged on an issue, task, incident or run sheet.</div>
            </div>
          )}

          {!loading && data && SECTIONS.map((sec) => {
            const rows = data[sec.key] || [];
            if (rows.length === 0) return null;
            const Icon = sec.icon;
            return (
              <div key={sec.key} className="border-b border-cmdBorder" data-testid={`person-refs-section-${sec.key}`}>
                <div className="px-5 py-3 flex items-center justify-between bg-white/[0.02]">
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-readyGreen" />
                    <span className="mc-label">{sec.label}</span>
                    <span className="font-mono-live text-xs text-zinc-500">({rows.length})</span>
                  </div>
                  <Link
                    to={sec.route}
                    onClick={onClose}
                    className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-zinc-400 hover:text-white"
                  >
                    Go <ExternalLink className="w-3 h-3" />
                  </Link>
                </div>
                <div>
                  {rows.map((d) => (
                    <div key={d.id} className="px-5 py-3 border-t border-cmdBorder/50 hover:bg-white/[0.02]" data-testid={`person-refs-row-${d.id}`}>
                      <div className="text-sm font-medium">{itemTitle(sec.key, d)}</div>
                      {itemSubtitle(sec.key, d) && (
                        <div className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{itemSubtitle(sec.key, d)}</div>
                      )}
                      {d.status && (
                        <div className="mt-1 text-[10px] uppercase tracking-widest font-bold text-zinc-400">
                          Status · <span className="text-white">{String(d.status).replace(/_/g, " ")}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </aside>
    </div>
  );
}
